import React, { FC, KeyboardEvent, useEffect, useMemo, useState } from "react";
import { useIMask } from "react-imask";
import { FormGroup, Input, Label } from "reactstrap";

import { useIsClientSide } from "@utils/hooks/isClient.hook";
import { ProductConfigurationVisualProps } from "@utils/productConfiguration/productConfiguration.types";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import "./licensePlate.scss";
import {
    LicensePlateConfiguration,
    LicensePlateTypes,
} from "./licensePlate.types";

const MAX_LENGTH = 8;

const LicensePlate: FC<
    ProductConfigurationVisualProps<LicensePlateConfiguration>
> = ({
    setConfigurationValid,
    onConfigurationChange,
    configuration,
}: ProductConfigurationVisualProps<LicensePlateConfiguration>) => {
    const { chosenLicensePlateText, chosenLicensePlateType } = configuration;
    const { t } = useGranite();
    const isClientSide = useIsClientSide();
    const [licensePlateValue, setLicensePlateValue] = useState<string>();
    const personalized = useMemo(
        () =>
            chosenLicensePlateType ===
            LicensePlateTypes.LICENSE_PLATE_CAR_PERSONALIZED,
        [chosenLicensePlateType],
    );

    const { ref, maskRef } = useIMask(
        {
            mask: "A-SSS-DDD",
            definitions: {
                A: /[A-Za-z\d]/,
                S: /[A-Za-z]/,
                D: /\d/,
            },
            placeHolderChar: "_",
            lazy: false,
        },
        {
            onAccept: value => {
                handleInputUpdate(value);
            },
        },
    );

    useEffect(() => {
        if (!chosenLicensePlateText) {
            return;
        }

        handleInputUpdate(chosenLicensePlateText);
    }, [chosenLicensePlateText]);

    useEffect(() => {
        if (chosenLicensePlateType) {
            return;
        }

        onConfigurationChange({
            chosenLicensePlateType:
                LicensePlateTypes.LICENSE_PLATE_CAR_STANDARD,
        });
    }, []);

    const preventIncorrectCharacters = (
        e: KeyboardEvent<HTMLInputElement>,
    ): void => {
        if (/[^A-Za-z0-9\-]+/.test(e.key)) {
            e.preventDefault();
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.type !== "change") {
            return;
        }

        let value = e.currentTarget.value;

        if (value.length > MAX_LENGTH) {
            value = value.substring(0, MAX_LENGTH);
        }

        handleInputUpdate(value.replace(/[^A-Za-z0-9\-]+/, ""));
    };

    const handleInputUpdate = (dirtyValue: string) => {
        const value = dirtyValue.toUpperCase();
        setLicensePlateValue(value);

        if (value !== maskRef?.current?.value) {
            if (maskRef?.current) {
                maskRef.current.value = value;
            }
            maskRef?.current?.updateControl();
        }

        if (
            chosenLicensePlateType ===
                LicensePlateTypes.LICENSE_PLATE_CAR_PERSONALIZED &&
            value.length
        ) {
            onConfigurationChange({ chosenLicensePlateText: value });
            return setConfigurationValid(true);
        }

        const valid = /[0-9A-Z]{1}-[A-Z]{3}-[0-9]{3}/.test(value);

        if (valid) {
            onConfigurationChange({ chosenLicensePlateText: value });
        }

        setConfigurationValid(valid);
    };

    return (
        <div className="custom-plate mt-4">
            <FormGroup>
                <Label for="licensePlate">
                    {t("ecommerce.licensePlate.fillInLicensePlate")}
                </Label>
                <Input
                    id="licensePlate"
                    type="text"
                    className="text-left"
                    value={isClientSide ? licensePlateValue || "" : ""}
                    innerRef={personalized ? undefined : ref}
                    maxLength={personalized ? MAX_LENGTH : undefined}
                    onKeyDown={event => preventIncorrectCharacters(event)}
                    onChange={e => handleChange(e)}
                />
            </FormGroup>
            <Label for="customPlate" className="mb-0">
                <Input
                    id="customPlate"
                    type="checkbox"
                    checked={personalized}
                    onChange={event => {
                        setLicensePlateValue("");
                        onConfigurationChange({ chosenLicensePlateText: "" });
                        setConfigurationValid(false);

                        if (event.target.checked) {
                            return onConfigurationChange({
                                chosenLicensePlateType:
                                    LicensePlateTypes.LICENSE_PLATE_CAR_PERSONALIZED,
                            });
                        }

                        return onConfigurationChange({
                            chosenLicensePlateType:
                                LicensePlateTypes.LICENSE_PLATE_CAR_STANDARD,
                        });
                    }}
                />
                <span>{t("ecommerce.licensePlate.personalized")}</span>
            </Label>
            <p
                dangerouslySetInnerHTML={{
                    __html: t("ecommerce.licensePlate.personalizedOption"),
                }}
            ></p>
        </div>
    );
};

export default LicensePlate;
